import React, { useState, useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import { COLORS_ADMIN_PAGE } from '../conf/constants';
import { Voucher } from './types/voucher.types';
import { useDebounce } from './hooks/useDebounce';
import { useVouchers } from './hooks/useVouchers';
import VoucherSidebar from './components/VoucherSidebar';
import VoucherDetails from './components/VoucherDetails';
import MunicipalityAssignment from './components/MunicipalityAssignment';
import CreateVoucherModal from './components/CreateVoucherModal';
import LoadingState from './components/LoadingState';
import ErrorState from './components/ErrorState';
import EmptyState from './components/EmptyState';
import NotificationSnackbar from './components/NotificationSnackbar';
import { useErrorHandler } from '../utils/useErrorHandler';

const PromotionsManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const sidebarWidth = '350px';

  const { 
    snackbar, 
    handleSnackbarClose, 
    handleErrorWithLogout, 
    showSuccessMessage, 
    showErrorMessage 
  } = useErrorHandler();

  const errorHandlers = useMemo(() => ({
    handleErrorWithLogout,
    showSuccessMessage,
    showErrorMessage
  }), [handleErrorWithLogout, showSuccessMessage, showErrorMessage]);

  const {
    vouchers,
    setVouchers,
    loading,
    error,
    selectedVoucher,
    setSelectedVoucher,
    isCreating,
    isToggling,
    isSaving,
    createVoucher,
    toggleVoucherStatus,
    saveVoucher,
    updateVoucherField,
    cancelChanges,
    retryFetch
  } = useVouchers(debouncedSearchTerm, errorHandlers);

  const currentVoucher = useMemo(() => {
    return vouchers.find((v:Voucher) => v.voucherId === selectedVoucher);
  }, [vouchers, selectedVoucher]);

  const handleVoucherSelect = useCallback((voucher: Voucher) => {
    if (selectedVoucher !== voucher.voucherId) {
      setSelectedVoucher(voucher.voucherId);
    }
  }, [selectedVoucher]);

  const handleCreateVoucher = useCallback(async (voucherData: {
    code: string;
    type: number;
    amount: number;
    cap: number;
    limit: number;
    isActive: boolean;
  }) => {
    try {
      await createVoucher(voucherData);
      setCreateModalOpen(false);
    } catch (err) {
      // Error is handled in the hook
    }
  }, []);

  const handleToggleVoucherStatus = useCallback(async () => {
    if (currentVoucher) {
      await toggleVoucherStatus(currentVoucher);
    }
  }, [currentVoucher]);

  const handleSaveVoucher = useCallback(async () => {
    if (currentVoucher) {
      await saveVoucher(currentVoucher);
    }
  }, [currentVoucher, saveVoucher]);

  const handleCancelChanges = useCallback(async () => {
    await cancelChanges();
  }, []);

  const handleVoucherFieldChange = useCallback((field: string, value: string | number) => {
    updateVoucherField(field, value);
  }, [updateVoucherField]);

  const handleUpdateVouchers = useCallback((updatedVouchers: Voucher[]) => {
    setVouchers(updatedVouchers);
  }, []);

  // Render main content based on state
  const renderMainContent = () => {
    if (loading) {
      return <LoadingState />;
    }

    if (error) {
      return (
        <ErrorState
          title="Error loading vouchers"
          message={error}
          onRetry={retryFetch}
        />
      );
    }

    if (!currentVoucher) {
      return <EmptyState message="Select a voucher to view details" />;
    }

    return (
      <Box>
        <VoucherDetails
          voucher={currentVoucher}
          onFieldChange={handleVoucherFieldChange}
        />

        <Box sx={{ mt: 3 }}>
          <MunicipalityAssignment
            currentVoucher={currentVoucher}
            vouchers={vouchers}
            onUpdateVouchers={handleUpdateVouchers}
            onSave={handleSaveVoucher}
            onCancel={handleCancelChanges}
            isSaving={isSaving}
            errorHandlers={errorHandlers} 
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', bgcolor: COLORS_ADMIN_PAGE.BACKGROUND }}>
      {/* Sidebar */}
      <VoucherSidebar
        sidebarWidth={sidebarWidth}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        vouchers={vouchers}
        loading={loading}
        selectedVoucher={selectedVoucher}
        onVoucherSelect={handleVoucherSelect}
        onCreateClick={() => setCreateModalOpen(true)}
        onToggleStatus={handleToggleVoucherStatus}
        currentVoucher={currentVoucher}
        isToggling={isToggling}
      />

      {/* Main Content */}
      <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
        {renderMainContent()}
      </Box>

      {/* Create Voucher Modal */}
      <CreateVoucherModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateVoucher}
        loading={isCreating}
      />

      {/* Snackbar for notifications */}
      <NotificationSnackbar
        snackbar={snackbar}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default PromotionsManagement;